/**
 * Pure decision logic for applying a partial layer reload (EID-61).
 *
 * The layer-provider diffs layer specs by id + content snapshot and only
 * (re)instantiates the layers that changed. Once the changed layers finish
 * loading, this module computes the merged layer state:
 *   - instances: freshly loaded instances for changed ids, previous
 *     instances carried over for unchanged ids, removed ids dropped
 *   - legend layers: recomputed over the merged set, in spec order
 *   - requested visible ids: changed/new layers contribute their spec
 *     `visible` flag; unchanged layers keep their current runtime visibility
 *     (so reloading layer A does not reset the user's toggle on layer B)
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

export interface LoadedLayerInstance {
  id: string;
  visible?: boolean;
  legend?: unknown;
  linked?: string;
}

export interface LayerApplyResult<T extends LoadedLayerInstance> {
  instances: Record<string, T>;
  legendLayers: T[];
  requestedVisibleIds: string[];
}

export const planLayerApply = <T extends LoadedLayerInstance>(args: {
  /** Ids in spec order — the full next layer set. */
  orderedIds: string[];
  /** Ids that were (re)loaded this pass. */
  changedIds: string[];
  /** Loaded instances keyed by id; null = failed validation/load. */
  loadedById: ReadonlyMap<string, T | null>;
  /** Instance registry before this pass. */
  prevInstances: Record<string, T>;
  /** Runtime-visible ids before this pass. */
  currentVisibleIds: string[];
}): LayerApplyResult<T> => {
  const changed = new Set(args.changedIds);
  const currentVisible = new Set(args.currentVisibleIds);
  const instances: Record<string, T> = {};
  const legendLayers: T[] = [];
  const requestedVisibleIds: string[] = [];
  for (const id of args.orderedIds) {
    const instance = changed.has(id)
      ? args.loadedById.get(id)
      : args.prevInstances[id];
    // Failed loads (validation errors) and unknown ids are dropped, matching
    // the previous full-reload behaviour where a null loader result was
    // simply not registered.
    if (!instance) continue;
    if (instance.id in instances) continue;
    instances[instance.id] = instance;
    const visible = changed.has(id)
      ? !!instance.visible
      : currentVisible.has(instance.id);
    if (visible) requestedVisibleIds.push(instance.id);
    if (instance.legend) legendLayers.push(instance);
  }
  return { instances, legendLayers, requestedVisibleIds };
};
